import React from 'react'
import { Select } from '@surecompanies/core_components'
import {
  FilterSection,
  FilterSectionTitle,
  FilterGroup,
  FilterLabel,
} from './styles'
import { toggleSelectAllInScope } from './helpers'
import SelectAllHeader from './SelectAllHeader'
import type { FilterUpdateFn } from './types'

interface DrawerSectionCompanyCarrierProps {
  clientPublicKeysValue: string[]
  companyOptions: { label: string; value: string }[]
  companyScopeValues: string[]
  setCompanySearch: (v: string) => void
  updateFilter: FilterUpdateFn
  carriersValue: string[]
  carrierOptions: string[]
  carrierScopeNames: string[]
  carriersLoading: boolean
  setCarrierSearch: (v: string) => void
}

export default function DrawerSectionCompanyCarrier({
  clientPublicKeysValue,
  companyOptions,
  companyScopeValues,
  setCompanySearch,
  updateFilter,
  carriersValue,
  carrierOptions,
  carrierScopeNames,
  carriersLoading,
  setCarrierSearch,
}: DrawerSectionCompanyCarrierProps) {
  return (
    <FilterSection>
      <FilterSectionTitle>Company & Carrier</FilterSectionTitle>

      <FilterGroup>
        <FilterLabel>Company</FilterLabel>
        <Select
          mode="multiple"
          placeholder="All Companies"
          value={clientPublicKeysValue}
          showSearch
          onSearch={setCompanySearch}
          dropdownRender={(menu) => (
            <>
              <SelectAllHeader
                selectedCount={clientPublicKeysValue.length}
                scopeValues={companyScopeValues}
                currentValues={clientPublicKeysValue}
                onToggle={() => {
                  const next = toggleSelectAllInScope(
                    clientPublicKeysValue,
                    companyScopeValues
                  )
                  updateFilter('clientPublicKeys', next as string[])
                }}
              />
              {menu}
            </>
          )}
          onChange={(value) => {
            if (Array.isArray(value)) {
              updateFilter('clientPublicKeys', value)
            }
          }}
          style={{ width: '100%' }}
          maxTagCount={clientPublicKeysValue.length >= 2 ? 0 : 'responsive'}
          maxTagPlaceholder={
            clientPublicKeysValue.length >= 2
              ? (omittedValues) => `${omittedValues.length} items selected`
              : undefined
          }
          filterOption={(input, option: any) => {
            const label = option?.label || option?.children
            if (typeof label === 'string') {
              return label.toLowerCase().includes(input.toLowerCase())
            }
            return false
          }}
        >
          {companyOptions.map((o) => (
            <Select.Option key={o.value} value={o.value}>
              {o.label}
            </Select.Option>
          ))}
        </Select>
      </FilterGroup>

      <FilterGroup>
        <FilterLabel>Carrier</FilterLabel>
        <Select
          mode="multiple"
          placeholder="All Carriers"
          value={carriersValue}
          loading={carriersLoading}
          onSearch={setCarrierSearch}
          dropdownRender={(menu) => (
            <>
              <SelectAllHeader
                selectedCount={carriersValue.length}
                scopeValues={carrierScopeNames}
                currentValues={carriersValue}
                onToggle={() => {
                  const next = toggleSelectAllInScope(
                    carriersValue,
                    carrierScopeNames
                  )
                  updateFilter('carrierNames', next as string[])
                }}
              />
              {menu}
            </>
          )}
          onChange={(value) => {
            const names = (Array.isArray(value) ? value : []).filter(
              (v): v is string => typeof v === 'string'
            )
            updateFilter('carrierNames', names)
          }}
          style={{ width: '100%' }}
          maxTagCount={carriersValue.length >= 2 ? 0 : 'responsive'}
          maxTagPlaceholder={
            carriersValue.length >= 2
              ? (omittedValues) => `${omittedValues.length} selected`
              : undefined
          }
          showSearch
          filterOption={(input, option: any) => {
            const haystack =
              (typeof option?.label === 'string' ? option.label : '') ||
              (typeof option?.children === 'string' ? option.children : '') ||
              (typeof option?.['data-search'] === 'string'
                ? option['data-search']
                : '') ||
              (typeof option?.props?.['data-search'] === 'string'
                ? option.props['data-search']
                : '')
            return haystack.toLowerCase().includes(input.toLowerCase())
          }}
        >
          {carrierOptions.map((name) => (
            <Select.Option key={name} value={name} data-search={name}>
              {name}
            </Select.Option>
          ))}
        </Select>
      </FilterGroup>
    </FilterSection>
  )
}
